"use client";

import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonLoading,
  useIonViewWillEnter
} from "@ionic/react";
import {
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete
} from "@mui/material";
import SummarizeIcon from '@mui/icons-material/Summarize';
import PrintIcon from '@mui/icons-material/Print';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import axios from "axios";
import { requestID, baseUrl, config } from "../../../../common/utils";
import { useBusinessContext } from "../../../../context/business-context";

// --- INTERFACES ---

interface LedgerEntry {
  id: string;
  coa_id: string;
  account_code: string;
  account_name: string;
  transaction_date: string;
  reference_no: string;
  narration: string;
  debit: string;
  credit: string;
  post_type: 'debit' | 'credit';
}

interface ReportSummary {
  total_debit: number;
  total_credit: number;
  net_balance: number;
}

interface LedgerAccount {
  coa_id: string;
  account_code: string;
  account_name: string;
  ledger_name?: string;
}

// --- COMPONENT ---

const SimpleLedgerReport: React.FC = () => {
  const { selectedBusinessId, selectedBusinessName } = useBusinessContext();
  
  // State
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<ReportSummary>({ total_debit: 0, total_credit: 0, net_balance: 0 });
  const [dates, setDates] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [ledgers, setLedgers] = useState<LedgerAccount[]>([]);
  const [selectedLedger, setSelectedLedger] = useState<string>("");
  const [loadingLedgers, setLoadingLedgers] = useState(false);

  // Fetch ledgers list
  const fetchLedgers = async () => {
    // if (!selectedBusinessId) return;
    
    try {
      setLoadingLedgers(true);
      const formData = new FormData();
      formData.append("requestID", requestID());
      //formData.append("business_id", selectedBusinessId);

      const response = await axios.post(
        baseUrl() + "Geacloud_ChartOfAccounts/getLedgerAccounts", 
        formData, 
        config
      );
      console.log({response})

      if (response.data.status) {
        setLedgers(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching ledgers:", error);
    } finally {
      setLoadingLedgers(false);
    }
  };

  const fetchLedgerData = async () => {
    // if (!selectedBusinessId || !selectedLedger) return;
    
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("business_id", selectedBusinessId);
      formData.append("startDate", dates.start);
      formData.append("endDate", dates.end);
      formData.append("ledger_id", selectedLedger);

      const response = await axios.post(
        baseUrl() + "Geacloud_ChartOfAccounts/getLedgerEntries", 
        formData, 
        config
      );
console.log( {response})
      if (response.data.status) {
        setEntries(response.data.data || []);
        setSummary(response.data.summary || { total_debit: 0, total_credit: 0, net_balance: 0 });
      } else {
        setEntries([]);
        setSummary({ total_debit: 0, total_credit: 0, net_balance: 0 });
      }
    } catch (error) {
      console.error("Error fetching ledger:", error);
      setEntries([]);
      setSummary({ total_debit: 0, total_credit: 0, net_balance: 0 });
    } finally {
      setLoading(false);
    }
  };

  useIonViewWillEnter(() => {
    fetchLedgers();
  });

  const handleLedgerChange = (event: any) => {
    const value = event.target.value;
    setSelectedLedger(value);
  };

  const getSelectedLedgerName = () => {
    if (!selectedLedger) return "";
    const ledger = ledgers.find(l => l.coa_id === selectedLedger);
    return ledger ? `${ledger.account_code} - ${ledger.account_name}` : "";
  };

  // Function to calculate running balance as we render
  let currentRunningBalance = 0;

  return (
    <IonPage className="pageDown">
      <IonHeader>
        <IonToolbar>
          <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ color: "#2c3e50", fontWeight: 700 }}>
                {selectedBusinessId ? `${selectedBusinessName?.toUpperCase()} - GENERAL LEDGER` : 'GENERAL LEDGER'}
              </Typography>
              {selectedLedger && (
                <Typography variant="caption" sx={{ color: "#64748b", mt: 0.5, display: 'block' }}>
                  Ledger: {getSelectedLedgerName()}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" startIcon={<PrintIcon />}>Print PDF</Button>
            </Stack>
          </Box>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <Box sx={{ p: 3, backgroundColor: "#f8fafc", minHeight: "100%" }}>
          
          {/* Header Filters */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="ledger-select-label">Select Ledger</InputLabel>
                    <Select
                      labelId="ledger-select-label"
                      label="Select Ledger"
                      value={selectedLedger}
                      onChange={handleLedgerChange}
                      disabled={loadingLedgers}
                      startAdornment={loadingLedgers ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                          <IonLoading message="Loading ledgers..." isOpen={loadingLedgers} />
                        </Box>
                      ) : null}
                    >
                      <MenuItem value="">
                        <em>All Ledgers</em>
                      </MenuItem>
                      {ledgers.map((ledger) => (
                        <MenuItem key={ledger.coa_id} value={ledger.coa_id}>
                          {ledger.account_code} - {ledger.account_name}
                          {ledger.ledger_name && (
                            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                              ({ledger.ledger_name})
                            </Typography>
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label="From Date"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={dates.start}
                    onChange={(e) => setDates({...dates, start: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label="To Date"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={dates.end}
                    onChange={(e) => setDates({...dates, end: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    startIcon={<SummarizeIcon />}
                    onClick={fetchLedgerData}
                    disabled={!selectedLedger}
                    sx={{ 
                      bgcolor: selectedLedger ? '#1e293b' : '#cbd5e1', 
                      '&:hover': { bgcolor: selectedLedger ? '#0f172a' : '#cbd5e1' },
                      height: '40px'
                    }}
                  >
                    Fetch
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<AccountTreeIcon />}
                    onClick={fetchLedgers}
                    sx={{ height: '40px' }}
                  >
                    Refresh Ledgers
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Alternative: Autocomplete for ledger selection */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'none' }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={ledgers}
                    getOptionLabel={(option) => `${option.account_code} - ${option.account_name}`}
                    value={ledgers.find(l => l.coa_id === selectedLedger) || null}
                    onChange={(event, newValue) => {
                      setSelectedLedger(newValue?.coa_id || "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Ledger"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <AccountTreeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    loading={loadingLedgers}
                    disabled={loadingLedgers}
                  />
                </Grid>
                {/* Rest of the date and button grid items */}
              </Grid>
            </CardContent>
          </Card>

          {/* Summary Stats - Only show when ledger is selected */}
          {selectedLedger && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', borderLeft: '4px solid #3b82f6' }}>
                  <Box sx={{ mr: 2, bgcolor: '#eff6ff', p: 1, borderRadius: 1 }}>
                    <AccountBalanceIcon sx={{ color: '#3b82f6' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>TOTAL DEBIT</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {summary.total_debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', borderLeft: '4px solid #ef4444' }}>
                  <Box sx={{ mr: 2, bgcolor: '#fef2f2', p: 1, borderRadius: 1 }}>
                    <AccountBalanceIcon sx={{ color: '#ef4444' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>TOTAL CREDIT</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {summary.total_credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ 
                  p: 2, 
                  display: 'flex', 
                  alignItems: 'center', 
                  borderLeft: '4px solid #10b981',
                  bgcolor: summary.net_balance >= 0 ? '#ffffff' : '#fff1f2'
                }}>
                  <Box sx={{ mr: 2, bgcolor: '#ecfdf5', p: 1, borderRadius: 1 }}>
                    <SummarizeIcon sx={{ color: '#10b981' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>NET BALANCE</Typography>
                    <Typography variant="h6" fontWeight={700} color={summary.net_balance < 0 ? 'error' : 'inherit'}>
                      {Math.abs(summary.net_balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      {summary.net_balance >= 0 ? ' Dr' : ' Cr'}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Data Table */}
          <Card sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table sx={{ minWidth: 650 }} size="small">
                <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>DATE</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>ACCOUNT</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>REFERENCE</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>NARRATION</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>DEBIT</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>CREDIT</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>BALANCE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!selectedLedger ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <AccountTreeIcon sx={{ fontSize: 48, color: '#cbd5e1' }} />
                          <Typography variant="body1" color="text.secondary">
                            Please select a ledger to view transactions
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Choose a ledger from the dropdown above
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <SummarizeIcon sx={{ fontSize: 48, color: '#cbd5e1' }} />
                          <Typography variant="body1" color="text.secondary">
                            No transactions found for {getSelectedLedgerName()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Try changing the date range or select a different ledger
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((row) => {
                      const dr = parseFloat(row.debit);
                      const cr = parseFloat(row.credit);
                      currentRunningBalance += (dr - cr);

                      return (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.transaction_date}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{row.account_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{row.account_code}</Typography>
                          </TableCell>
                          <TableCell>{row.document_id || row.reference_no}</TableCell>
                          <TableCell sx={{ maxWidth: 250, fontSize: '0.8rem' }}>{row.narration}</TableCell>
                          <TableCell align="right" sx={{ color: dr > 0 ? 'inherit' : '#cbd5e1' }}>
                            {dr > 0 ? dr.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: cr > 0 ? 'inherit' : '#cbd5e1' }}>
                            {cr > 0 ? cr.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {Math.abs(currentRunningBalance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                              {currentRunningBalance >= 0 ? 'Dr' : 'Cr'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <IonLoading isOpen={loading} message="Fetching ledger entries..." />
          <IonLoading isOpen={loadingLedgers} message="Loading ledgers list..." />
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default SimpleLedgerReport;